import { createClient } from "@supabase/supabase-js";
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import { REACT_APP_SUPABASE_URL, REACT_APP_SUPABASE_KEY } from "./constants";
import { compose as C, apply, invoker, map as m,filter as f,identity as id, unapply,
  pick, omit, toPairs,chain, indexOf, juxt, fromPairs,zipObj,props,mergeLeft,
  prop as p} from "ramda";

export const supabase = createClient(REACT_APP_SUPABASE_URL,REACT_APP_SUPABASE_KEY);

const invokeAll = (xs,data) => C(...m(
  apply(unapply(([method, ...args]) => invoker(args.length, method)(...args))),
  xs))(data);

export const supabaseApi = createApi({
    baseQuery: fakeBaseQuery(),
    tagTypes: ["decisions","decision_types","parts","roles","participant_roles",
      "my_role","participants","data","translations","user","landing_content","landing_sections"],
    endpoints: build => ({
      user: build.query({
        providesTags: ["user"],
        queryFn: async (id) => {
          const response = await invokeAll([
            ["single"],
            ["eq","id",id],
            ['select',"*"],
            ['from',"profiles"]
          ],supabase);
          return pick([response.error ?"error" : "data"],response);
        },
      }),
      decisions: build.query({
        providesTags: (result, error, page) => [{ type: 'decisions', id: 'list' },
          ...(result && result.data ? result.data.map(({ id }) => ({ type: 'decisions', id })) : []) ],
        queryFn: async (obj) => {
          const response = await invokeAll(!obj ? [
            ['select',"id, name,name_eng,done_item,done,show_until,description,description_eng,decision_type_id,decision_types(parts(part_bias(biases(*)))),participants(*)"],
            ['from',"decisions_with_participants"]
          ] : [
            ...m(x => ["eq",...x],toPairs(obj)),
            ["gt","decision_types.parts.id",99],
            ['select',"*,decision_types(parts(*,part_bias(biases(*)),activities_with_types(*)))"],
            ['from',"decisions_with_participants"]
          ],supabase);
          return pick([response.error ? "error" : "data"],response);
        },
        onCacheEntryAdded: async (arg,{ updateCachedData, cacheDataLoaded, cacheEntryRemoved }) => {
          await cacheDataLoaded;
          const handleInserts = (payload) => {
            updateCachedData((x) => {
              const index = indexOf(payload.old.id,x.map(x => x.id));
              if (index !== -1) x[index] = payload.new 
              else x.push(payload.new);
            })}
          const handleDeletes = (payload) => {updateCachedData((x) => {
              const index = indexOf(payload.old.id,x.map(x => x.id));
              if (index !== -1) x.splice(index,1)
              })}
          const handleUpdates = (payload) => {
            updateCachedData((x) => {
              const index = indexOf(payload.old.id, x.map(x => x.id));
              if (index !== -1) x[index] = { ...x[index], ...payload.new };
            })
          }
          const channel = supabase
            .channel('decisions')
            .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'decisions' }, handleInserts)
            .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'decisions' }, handleUpdates)
            .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'decisions' }, handleDeletes)
            .subscribe()
          
          await cacheEntryRemoved;
          supabase.removeChannel(channel);
        },
      }),
      translations: build.query({
        providesTags: ["translations"],
        queryFn: async () => {
          const response = await invokeAll([
            ['select',"*"],
            ['from',"translations"]
          ],supabase);
          const mapped = C(
            zipObj(["et","en"]),
            juxt([
              C(fromPairs,m(props(["key","et"]))),
              C(fromPairs,m(props(["key","en"])))
            ])
          );
          return response.error ? pick(["error"],response) : {data:mapped(response.data)};
        },
      }),
      decisionTypes: build.query({
        providesTags: ["decision_types"],
        queryFn: async () => {
          const response = await invokeAll([
            ['select',"*"],
            ['from',"decision_types"]
          ],supabase);
          return pick([response.error ?"error" : "data"],response);
        },
      }),
      parts: build.query({
        providesTags: ["parts"],
        queryFn: async (id) => {
          const query = (id ? [["eq","decision_type_id",id]] : []).concat([
            ["gt","id",99],
            ['select',"*, activities_with_types(*, activity_inputs!activity_inputs_activity_id_fkey(*))"],
            ['from',"parts"]
          ])
          const response = await invokeAll(query,supabase);
          return pick([response.error ?"error" : "data"],response);
        },
      }),
      participants: build.query({
        providesTags: ["participants"],
        queryFn: async (obj) => {
          const query = [
            ...m(x => ["eq",...x],toPairs(obj)),
            ['select',"*, participant_roles(participant_id, role_id, id)"],
            ['from',"participants"]
          ]
          const response = await invokeAll(query,supabase);
          return pick([response.error ?"error" : "data"],response);
        },

        onCacheEntryAdded: async (arg, {updateCachedData, cacheDataLoaded, cacheEntryRemoved,getState }) => {
          await cacheDataLoaded;
          await cacheDataLoaded;
          const handleParticipantsUpdate = (payload) => {
            updateCachedData((currentData) => {
              const index = currentData.findIndex((item) => item.id === payload.old.id);
              if (index !== -1) currentData[index] = payload.new;
              else currentData.push(payload.new);
            });
          };
          const handleParticipantsDelete = (payload) => {
            updateCachedData((currentData) => {
              const index = currentData.findIndex((item) => item.id === payload.old.id);
              if (index !== -1) currentData.splice(index, 1);
            });
          };
          const participantsChannel = supabase
            .channel("participants")
            .on("postgres_changes", { event: "INSERT", schema: "public", table: "participants" }, handleParticipantsUpdate)
            .on("postgres_changes", { event: "UPDATE", schema: "public", table: "participants" }, handleParticipantsUpdate)
            .on("postgres_changes", { event: "DELETE", schema: "public", table: "participants" }, handleParticipantsDelete)
            .subscribe();
      

          const handleParticipantRolesUpdate = async (payload) => {
            const decision_id = getState().ui.decision;
            
            const { data: new_data } = await supabase
                .from("participants")
                .select("*, participant_roles(participant_id, role_id, id)")
                .eq("decision_id", decision_id)

            updateCachedData((currentCacheData) => {
              currentCacheData.push(...new_data);
            });
          }

          const participantRolesChannel = supabase
            .channel("participant_roles")
            .on("postgres_changes", 
              { event: "*", schema: "public", table: "participant_roles" }, 
              handleParticipantRolesUpdate)
            .subscribe();
        
          // Cleanup on cache entry removal
          await cacheEntryRemoved;
          await participantsChannel.unsubscribe();
          await participantRolesChannel.unsubscribe();
        }
      }),
      myRoles: build.query({
        providesTags: ["my_role"],
        queryFn: async (obj) => {
          const query = [
            ...m(x => ["eq",...x],toPairs(obj)),
            ['select',"*"],
            ['from',"participants_with_id_and_email"]
          ]
          const response = await invokeAll(query,supabase);
          return pick([response.error ?"error" : "data"],response);
        },
      }),
      roles: build.query({
        providesTags: ["roles"],
        queryFn: async () => {
          const query = [
            ['select',"*"],
            ['from',"roles"]
          ];
          const response = await invokeAll(query,supabase);
          return pick([response.error ?"error" : "data"],response);
        },
      }),
      data: build.query({
        providesTags: ["data"],
        queryFn: async (id) => {
          const query = (id ? [["eq","decision_id",id]] : []).concat([
            ['select',"*, activities_with_types(*, activity_inputs!activity_inputs_activity_id_fkey(*))"],
            ['from',"data"]
          ])
          const response = await invokeAll(query,supabase);
          return pick([response.error ?"error" : "data"],response);
        },
        onCacheEntryAdded: async (arg,{ updateCachedData, cacheDataLoaded, cacheEntryRemoved }) => {
          await cacheDataLoaded;
          const handleInserts = (payload) => {
            updateCachedData((x) => {
              // Only push if this ID doesn't already exist in cache (avoid duplicates)
              if (!x.find(item => item.id === payload.new.id)) {
                x.push(payload.new);
              }
            })
          }
          const handleDeletes = (payload) => {updateCachedData((x) => {
            const index = indexOf(payload.old.id,x.map(x => x.id));
            if (index !== -1) x.splice(index,1)
          })}
          const handleUpdates = (payload) => {updateCachedData((x) => {
            const index = indexOf(payload.old.id,x.map(x => x.id));
            if (index !== -1) x[index] = payload.new;
          })}
          const channel = supabase
            .channel('data')
            .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'data' }, handleInserts)
            .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'data' }, handleUpdates)
            .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'data' }, handleDeletes)
            .subscribe()
          
          await cacheEntryRemoved;
          supabase.removeChannel(channel);
        },
      }),
      upsertParticipantRole: build.mutation({
        queryFn: async (obj) => {
          const result = await supabase.from('participant_roles').upsert(obj).select();
          return pick([result.error ?"error" : "data"],result)},
        invalidatesTags: ["participants","my_role","decisions"],
      }),
      deleteParticipantRole: build.mutation({
        queryFn: async (id) => {
          const result = await supabase.from('participant_roles').delete().eq('id', id).select();
          return pick([result.error ?"error" : "data"],result)},
        invalidatesTags: ["participants","my_role"],
      }),
      insertDecision: build.mutation({
        queryFn: async (obj) => {
          const result = await supabase.from('decisions').insert(obj).select();
          return result},
        invalidatesTags: [{ type: 'decisions', id: 'list' }],
      }), 
      upsertParticipants: build.mutation({
        queryFn: async (obj) => {
          const result = await supabase.from('participants').upsert(obj).select();
          return result},
        invalidatesTags:(result, error, { id }) => {
          return [
          { type: 'decisions', id: 'list' }]},
      }), 
      deleteParticipant: build.mutation({
        queryFn: async ({id}) => {
          const result = await supabase.from('participants').delete().eq('id', id).select();
          return pick([result.error ?"error" : "data"],result)},
        invalidatesTags: ["participants","decisions"],
      }),
      updateDecision: build.mutation({
        queryFn: async (obj) => {
          const result = await supabase.from('decisions').update(omit(["id"],obj)).eq('id', obj.id);
          return result},
        invalidatesTags: (result, error, { id }) => [
          { type: 'decisions', id }, 
          { type: 'decisions', id: 'list' },
        ],
      }),
      updateUser: build.mutation({
        queryFn: async (obj) => {
          const result = await supabase.from('profiles').update(omit(["id"],obj)).eq('id', obj.id);
          return result},
        invalidatesTags: (result, error, { id }) => [
          { type: 'user', id }, 
        ],
      }),
      upsertData: build.mutation({
        queryFn: async (data) => {
          const responses = await Promise.all(m(x => supabase.from('data').upsert(x),data));
          const mapped = { 
            error: C(f(id),m(p('error')))(responses), 
            data: C(f(id),m(p('error')))(responses)
          }
          return pick([mapped.error.length ? "error" : "data"],mapped);
        },
        invalidatesTags: (result, error, { decision_id }) => [{ type: 'decisions', id:decision_id }],  // REMOVED "data" tag
      }),
      deleteData: build.mutation({
        queryFn: async (id) => {
          const result = await supabase.from('data').delete().eq('id', id).select();
          return pick([result.error ?"error" : "data"],result)},
        invalidatesTags: ["data"],
      }),
      landingContent: build.query({
        providesTags: ["landing_content"],
        queryFn: async () => {
          const result = await supabase.from('landing_content').select('*').order('id');
          return pick([result.error ? "error" : "data"], result);
        },
      }),
      updateLandingContent: build.mutation({
        queryFn: async ({ key, value_et, value_en }) => {
          const result = await supabase
            .from('landing_content')
            .update({ value_et, value_en, updated_at: new Date().toISOString() })
            .eq('key', key)
            .select()
            .single();
          return pick([result.error ? "error" : "data"], result);
        },
        invalidatesTags: ["landing_content"],
      }),
      landingSections: build.query({
        providesTags: ["landing_sections"],
        queryFn: async () => {
          const result = await supabase
            .from('landing_sections')
            .select('*')
            .order('order');
          return pick([result.error ? "error" : "data"], result);
        },
      }),
      updateLandingSection: build.mutation({
        queryFn: async ({ id, label_et, label_en, content_et, content_en }) => {
          const result = await supabase
            .from('landing_sections')
            .update({ label_et, label_en, content_et, content_en, updated_at: new Date().toISOString() })
            .eq('id', id)
            .select()
            .single();
          return pick([result.error ? "error" : "data"], result);
        },
        invalidatesTags: ["landing_sections"],
      }),
    })
})
export const {
  useDecisionsQuery,useUpdateDecisionMutation,useInsertDecisionMutation,
  useMyRolesQuery,useUpsertParticipantRoleMutation,useDeleteParticipantRoleMutation,
  useParticipantsQuery,useUpsertParticipantsMutation, useDeleteParticipantMutation,
  useUserQuery,useUpdateUserMutation,
  useRolesQuery,
  
  useDataQuery,useUpsertDataMutation,useDeleteDataMutation,
  useDecisionTypesQuery,
  useTranslationsQuery,
  usePartsQuery,
  useLandingContentQuery,
  useUpdateLandingContentMutation,
  useLandingSectionsQuery,
  useUpdateLandingSectionMutation,
} = supabaseApi;